import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Avatar, Dropdown, DropdownItem } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';

function isAdminRoute(pathname: string) {
  if (pathname.startsWith('/soporte/admin')) return true;
  const student = ['/dashboard', '/mis-cursos', '/mis-certificados', '/calendario', '/soporte', '/profile'];
  if (student.some((p) => pathname === p || (pathname.startsWith(`${p}/`) && !pathname.startsWith('/soporte/admin')))) return false;
  if (pathname === '/expediente' && !pathname.includes('/admin')) return false;
  return pathname.startsWith('/administracion') || pathname.startsWith('/apariencia') || pathname.startsWith('/courses') || pathname.startsWith('/categorias') || pathname.startsWith('/library') || pathname.startsWith('/media') || pathname.startsWith('/h5p') || pathname.startsWith('/evaluaciones') || pathname.startsWith('/preguntas') || pathname.startsWith('/calificaciones') || pathname.startsWith('/progreso') || pathname.startsWith('/expediente') || pathname.startsWith('/inscripciones') || pathname.startsWith('/grupos') || pathname.startsWith('/credenciales') || pathname.startsWith('/usuarios') || pathname.startsWith('/roles') || pathname.startsWith('/permisos') || pathname.startsWith('/campos-personalizados') || pathname.startsWith('/desarrollo') || pathname.startsWith('/soporte/admin');
}

export function PrimaryNav() {
  const { user, logout } = useAuth();
  const { canAccessAdmin, hasPermission } = usePermissions();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const canManageSupport = hasPermission(PERMISSIONS.SOPORTE_VER);
  const canViewOwnSupport = hasPermission(PERMISSIONS.MIS_SOPORTE_VER);
  const showSupportNav = canManageSupport || canViewOwnSupport;
  const supportNavPath = canManageSupport ? '/soporte/admin' : '/soporte';

  const isSupportNavActive =
    canManageSupport
      ? location.pathname === '/soporte/admin' || location.pathname.startsWith('/soporte/admin/')
      : location.pathname === '/soporte' || location.pathname.startsWith('/soporte/');

  const isAdministration = isAdminRoute(location.pathname);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `lms-primary-nav-link ${isActive ? 'lms-primary-nav-link-active' : ''}`;

  const mobileLinkClass = (active: boolean) =>
    active
      ? 'rounded-lg bg-dash-primary/10 px-3 py-3 text-sm font-bold text-dash-primary'
      : 'rounded-lg px-3 py-3 text-sm font-medium text-dash-on-surface-variant hover:bg-white/80 hover:text-dash-primary';

  return (
    <header
      className="lms-primary-nav fixed top-0 right-0 left-0 z-sticky font-sans"
    >
      <div className="lms-site-admin-hub-container flex h-full w-full items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-8 md:gap-12">
          <Link to="/dashboard" className="shrink-0 text-xl font-extrabold tracking-tight text-dash-primary md:text-2xl">
            LMS
          </Link>

          <nav className="hidden items-center gap-8 md:flex" aria-label="Navegación principal">
            {hasPermission('dashboard.access') && (
              <NavLink to="/dashboard" className={linkClass} end>
                Tablero
              </NavLink>
            )}
            {hasPermission('mis-cursos.ver') && (
              <NavLink to="/mis-cursos" className={linkClass}>
                Mis cursos
              </NavLink>
            )}
            {hasPermission('mis-cursos.ver') && (
              <NavLink to="/calendario" className={linkClass}>
                Calendario
              </NavLink>
            )}
            {showSupportNav && (
              <NavLink
                to={supportNavPath}
                className={() => linkClass({ isActive: isSupportNavActive })}
              >
                Mesa de ayuda
              </NavLink>
            )}
            {canAccessAdmin && (
              <NavLink to="/administracion" className={linkClass}>
                Administración
              </NavLink>
            )}
          </nav>
        </div>

        <div className="flex shrink-0 items-center gap-3 md:gap-6">
          <div className="hidden items-center gap-1 sm:flex">
            <Link to="/profile" className="lms-primary-nav-icon inline-flex" aria-label="Configuración">
              <span className="material-symbols-outlined text-[1.35rem]" aria-hidden="true">
                settings
              </span>
            </Link>
          </div>

          {user && (
            <Dropdown
              align="right"
              trigger={
                <button
                  type="button"
                  className="rounded-full p-0.5 ring-2 ring-dash-primary/20 ring-offset-2 transition-transform hover:scale-105"
                  aria-label="Menú de cuenta"
                >
                  <Avatar name={user.name} size="sm" />
                </button>
              }
            >
              <DropdownItem href="/profile">Mi perfil</DropdownItem>
              <DropdownItem onClick={() => logout()}>Cerrar sesión</DropdownItem>
            </Dropdown>
          )}

          <button
            type="button"
            className="lms-primary-nav-icon inline-flex md:hidden"
            aria-expanded={mobileOpen}
            aria-label="Abrir menú"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span className="material-symbols-outlined text-[1.5rem]" aria-hidden="true">
              {mobileOpen ? 'close' : 'menu'}
            </span>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-white/30 bg-white/95 backdrop-blur-md md:hidden">
          <nav className="flex flex-col px-5 py-3" aria-label="Navegación móvil">
            {hasPermission('dashboard.access') && (
              <NavLink to="/dashboard" className={({ isActive }) => mobileLinkClass(isActive)} end onClick={() => setMobileOpen(false)}>
                Tablero
              </NavLink>
            )}
            {hasPermission('mis-cursos.ver') && (
              <NavLink to="/mis-cursos" className={({ isActive }) => mobileLinkClass(isActive)} onClick={() => setMobileOpen(false)}>
                Mis cursos
              </NavLink>
            )}
            {hasPermission('mis-cursos.ver') && (
              <NavLink to="/calendario" className={({ isActive }) => mobileLinkClass(isActive)} onClick={() => setMobileOpen(false)}>
                Calendario
              </NavLink>
            )}
            {showSupportNav && (
              <NavLink
                to={supportNavPath}
                className={() => mobileLinkClass(isSupportNavActive)}
                onClick={() => setMobileOpen(false)}
              >
                Mesa de ayuda
              </NavLink>
            )}
            {canAccessAdmin && (
              <NavLink to="/administracion" className={({ isActive }) => mobileLinkClass(isActive || isAdministration)} onClick={() => setMobileOpen(false)}>
                Administración
              </NavLink>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
